import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { applyPackState } from "../components/dispatch/pack-state";
import { ArrowLeft, AlertTriangle, CheckCircle, Info } from "lucide-react";
import { refundsViaChannel } from "../components/helpers/channelRefunds";
import { useTranslation } from "../i18n";
import { toast } from "react-toastify";
import { playComplete, playError } from "../components/helpers/audioPlayer";
import DispatchOrderPack from "../components/dispatch/dispatch-order-pack";
import DispatchCloseOrder from "../components/dispatch/dispatch-close-order";
import { DispatchSessionProvider } from "../components/dispatch/dispatch-session-chrome";
import FlowLoader from "../components/FlowLoader";
import { postResolution, resolveAttribution } from "../components/dispatch/resolve-api";
import { getAutoPrint, fetchTodayStats } from "../components/dispatch/dispatchSession";
import type { DispatchStats } from "../components/dispatch/dispatchSession";
import type { CourierBookingResult, DeskInfo, DispatchBox, DispatchOrder, DispatchOrderItem } from "../types/Dispatch";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { useAccess } from "../access/AccessContext";
import { isManagerPlus, readUserPosition } from "../access/positions";
import { displayItemCode } from "../components/dispatch/item-code";
import "../styles/dispatch.css";

// Flagged-order resolution. The packing surface IS the main dispatch pack
// screen (DispatchOrderPack in resolveMode) so leaders get the identical
// experience — box recommendation, real box scanning, override telemetry,
// ship-in-packaging fast lane, the lot. This page wraps it with the
// resolve-specific pieces: locked already-shipped units, the refund
// summary, and the resolution persistence.
//
// Refund bookkeeping: inside the pack screen a refunded unit is stored as
// missingCount (the pack's "not shipped, takes no box space" counter,
// relabelled "Refund" in resolveMode). This page converts missingCount →
// refundCount when persisting.

// The desk record handed to the pack screen. Only the deskId comes from the
// leader's session — the WAREHOUSE is the order's own, never the reader's.
//
// The pack screen drives its box catalogue, dispatch settings and shipping
// tiers off deskInfo.warehouse, so a wrong value doesn't just fail the box
// scan (`box_wrong_warehouse` — box barcodes are unique per site), it
// recommends boxes that aren't on the bench and prices against another
// site's tiers. This page used to fall back to a hard-coded "Newport"
// whenever the leader had no dispatch desk in sessionStorage, which is the
// normal case: they arrive from the Flagged list without scanning into a
// desk, and sessionStorage is per-tab anyway. A Pembroke leader resolving a
// Pembroke order therefore scanned PEMBOX7 into a Newport pack screen and
// was told it belonged to another warehouse (order 337489, 2026-08-20).
//
// A flagged order can only be resolved at the site physically holding it,
// so the order's warehouse is authoritative; the desk session and the
// signed-in user's site are fallbacks for an order that carries none.
const getResolveDeskId = (): string => {
    try {
        const userData = window.localStorage.getItem("user");
        const uid = userData ? (JSON.parse(userData).uid || "unknown") : "unknown";
        const stored = window.sessionStorage.getItem(`dispatch-desk-${uid}`);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed?.deskId) return parsed.deskId;
        }
    } catch { /* fallthrough */ }
    return "LEADER";
};

// Fallbacks, in order, for an order with no warehouse on it: the desk the
// leader scanned into this tab, then their own site. Never a literal.
const getFallbackWarehouse = (): string => {
    try {
        const userData = window.localStorage.getItem("user");
        const parsed = userData ? JSON.parse(userData) : null;
        const uid = parsed?.uid || "unknown";
        const stored = window.sessionStorage.getItem(`dispatch-desk-${uid}`);
        if (stored) {
            const desk = JSON.parse(stored);
            if (typeof desk?.warehouse === "string" && desk.warehouse) return desk.warehouse;
        }
        if (typeof parsed?.warehouse === "string" && parsed.warehouse) return parsed.warehouse;
    } catch { /* fallthrough */ }
    return "";
};

export const getResolveDeskInfo = (order: DispatchOrder | null): DeskInfo => ({
    warehouse: order?.warehouse || getFallbackWarehouse(),
    deskId: getResolveDeskId(),
});

// Snapshot taken when the leader opens a flagged order: how many units of
// each item the original dispatch operator already shipped. These are locked
// — the working order's quantities are SHRUNK by them, so the pack screen
// only ever deals with the units the leader controls.
type OriginalShipped = Record<string, number>;

const buildOriginalShipped = (order: DispatchOrder): OriginalShipped => {
    const map: OriginalShipped = {};
    for (const item of order.items) {
        map[item.item_id] = (item.scannedCount || 0) + (item.barcodeDamagedCount || 0);
    }
    return map;
};

// User-flagged ⇔ the order was flagged BEFORE it was packed (Packing Issue /
// Label Issue): nothing has left the building, so the leader gets full
// control of every unit. Auto-flagged ⇔ the order already shipped with
// missing/damaged units (packed_at set): the shipped units are locked and
// only the flagged remainder needs a decision.
const isUserFlaggedOrder = (order: DispatchOrder): boolean => !order.packed_at;

// Build the working order the pack screen operates on.
//   User-flagged: full quantities; keep the original desk scans so the
//   leader sees what was packed (they re-box with real boxes).
//   Auto-flagged: quantities shrunk by the locked shipped units; counts
//   reset — the leader decides the remainder (ship or refund). Lines with
//   nothing left to decide drop out entirely (merged back on persist).
const buildWorkingOrder = (order: DispatchOrder, locked: OriginalShipped, userFlagged: boolean): DispatchOrder => {
    const items: DispatchOrderItem[] = [];
    for (const item of order.items) {
        if (userFlagged) {
            items.push({
                ...item,
                scannedCount: item.scannedCount || 0,
                barcodeDamagedCount: item.barcodeDamagedCount || 0,
                assignedBoxes: Array.isArray(item.assignedBoxes) ? [...item.assignedBoxes] : [],
                missingCount: 0,
                damagedCount: 0,
                wrongItemCount: 0,
                refundCount: 0,
                scanStatus: ((item.scannedCount || 0) + (item.barcodeDamagedCount || 0)) >= item.quantity
                    ? "scanned" as const : "pending" as const,
            });
            continue;
        }
        const remaining = item.quantity - (locked[item.item_id] || 0);
        if (remaining <= 0) continue;
        items.push({
            ...item,
            quantity: remaining,
            scannedCount: 0,
            barcodeDamagedCount: 0,
            assignedBoxes: [],
            missingCount: 0,
            damagedCount: 0,
            wrongItemCount: 0,
            refundCount: 0,
            scanStatus: "pending" as const,
        });
    }
    return { ...order, items };
};

// Re-exported so the flagged-resolve screen stays the documented home of
// the restore for its tests and callers; the implementation is shared with
// the dispatch desk's reopen.
export { applyPackState };

const formatMoney = (value: number, currency: string) => {
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
    } catch {
        return `${currency} ${value.toFixed(2)}`;
    }
};

// The POST /orders/:id/resolve wrapper lives in resolve-api.ts, shared
// with the flagged list's quick actions; re-exported so this page stays
// the documented home for its existing tests and callers.
export { postResolution };
export type { ResolveOutcome } from "../components/dispatch/resolve-api";

const DispatchFlaggedResolve = () => {
    usePageTitle("resolve_order");
    const { t } = useTranslation();
    const navigate = useNavigate();
    const readOnly = useReadOnly();
    // E9: refund lines need dispatch.flagged.refund on the BE; without it the
    // pack screen's per-item Refund is disabled up front, because the carrier
    // label is booked BEFORE resolve — a refusal there would strand a live
    // consignment on a still-flagged order.
    const { authoritative, canWrite } = useAccess();
    const canRefund = authoritative ? canWrite("dispatch.flagged.refund") : isManagerPlus(readUserPosition());
    const { orderId } = useParams<{ orderId: string }>();

    // The flagged order as the BE holds it — the merge target on persist.
    const [flaggedOrder, setFlaggedOrder] = useState<DispatchOrder | null>(null);
    // The pack screen's working copy (quantities shrunk for auto-flagged).
    const [order, setOrder] = useState<DispatchOrder | null>(null);
    // The in-flight fetch and a genuine miss must render DIFFERENTLY:
    // before this existed the page's first paint hit the "no order found"
    // fallback and every open flashed the error until the round-trip
    // landed.
    const [loadState, setLoadState] = useState<"loading" | "loaded" | "error">("loading");
    const [boxes, setBoxes] = useState<DispatchBox[]>([{ id: 1, items: [] }]);
    // A restored pack snapshot can arrive already complete (packer flagged
    // after scanning the last item). Auto-booking off the back of that
    // would ship the leader's order before they had looked at it, so the
    // restored case always waits for an explicit Complete — same reasoning
    // as the dispatch page's returnedFromShip.
    const [restoredFromSnapshot, setRestoredFromSnapshot] = useState(false);
    const [view, setView] = useState<"pack" | "refund-summary" | "confirm-ship">("pack");
    // Same per-user daily totals the dispatch bench shows — resolving a
    // flag is packing and shipping, and it bumps the same counters.
    const [stats, setStats] = useState<DispatchStats>({ itemsScanned: 0, ordersShipped: 0 });
    useEffect(() => { fetchTodayStats(apiFetch).then(setStats); }, []);
    const [courierResult, setCourierResult] = useState<CourierBookingResult | null>(null);
    const [shippingRefund, setShippingRefund] = useState("");
    const [success, setSuccess] = useState(false);
    // Recomputed when the order lands — its warehouse is what scopes the
    // pack screen's boxes, settings and tiers.
    const deskInfo = useMemo(() => getResolveDeskInfo(flaggedOrder), [flaggedOrder]);

    useEffect(() => {
        if (!orderId) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}`, {});
                if (!alive) return;
                if (!res.ok) { setLoadState("error"); return; }
                const data = await res.json();
                if (!alive) return;
                if (data?.order) {
                    const src: DispatchOrder = data.order;
                    setFlaggedOrder(src);
                    const userFlagged = isUserFlaggedOrder(src);
                    const locked = userFlagged ? {} : buildOriginalShipped(src);
                    const working = buildWorkingOrder(src, locked, userFlagged);
                    const restored = userFlagged
                        ? applyPackState(working, src.latest_flag?.pack_state)
                        : null;
                    setOrder(restored ? restored.order : working);
                    if (restored) {
                        setBoxes(restored.boxes);
                        setRestoredFromSnapshot(true);
                    }
                    setLoadState("loaded");
                } else {
                    setLoadState("error");
                }
            } catch { if (alive) setLoadState("error"); }
        })();
        return () => { alive = false; };
    }, [orderId]);

    const isUserFlagged = flaggedOrder ? isUserFlaggedOrder(flaggedOrder) : false;
    const flagInfo = useMemo(() => {
        const f = flaggedOrder?.latest_flag ?? null;
        if (!f?.flagged_reason) return null;
        return { reason: f.flagged_reason, by: f.flagged_by_name };
    }, [flaggedOrder]);

    // Leader-shipped units in the working order.
    const shippedUnits = (i: DispatchOrderItem) => (i.scannedCount || 0) + (i.barcodeDamagedCount || 0);
    // Refunds live in missingCount while inside the pack screen.
    const refundUnits = (i: DispatchOrderItem) => i.missingCount || 0;

    const itemsRefundTotal = order
        ? order.items.reduce((s, i) => s + refundUnits(i) * (i.unit_price || 0), 0)
        : 0;
    const shippingRefundValue = (() => {
        const n = parseFloat(shippingRefund);
        return Number.isFinite(n) && n > 0 ? n : 0;
    })();
    const totalRefund = itemsRefundTotal + shippingRefundValue;

    // Merge the leader's decisions back onto the ORIGINAL order shape so
    // Search Orders sees both the original and the resolved units:
    //   scanned/barcode-damaged = original + leader; refunds persist as
    //   missingCount (the established Search-Orders convention) AND drive
    //   the refund_lines below.
    const buildResolvedOrder = (): DispatchOrder | null => {
        if (!order || !flaggedOrder) return null;
        return {
            ...flaggedOrder,
            items: flaggedOrder.items.map(origItem => {
                const w = order.items.find(i => i.item_id === origItem.item_id);
                if (!w) return { ...origItem, missingCount: 0 };
                const origScanned = isUserFlagged ? 0 : (origItem.scannedCount || 0);
                const origBD = isUserFlagged ? 0 : (origItem.barcodeDamagedCount || 0);
                return {
                    ...origItem,
                    scannedCount: origScanned + (w.scannedCount || 0),
                    barcodeDamagedCount: origBD + (w.barcodeDamagedCount || 0),
                    missingCount: refundUnits(w),
                    refundCount: refundUnits(w),
                };
            }),
        };
    };

    // POST /orders/:id/resolve with the leader's per-line refund decisions.
    const persistResolution = async (resolved: DispatchOrder) => {
        const refundLines = resolved.items
            .filter(i => i.line_position !== undefined)
            .map(i => ({
                line_position: i.line_position,
                refund_count: i.refundCount || 0,
            }));
        // The response is load-bearing: this writes the refund lines and
        // clears the flag. Reporting success when the BE refused would tell
        // a leader money was refunded when nothing was persisted — the order
        // stays flagged and no refund exists.
        return postResolution(resolved.order_id, {
            ...resolveAttribution(),
            refund_lines: refundLines,
        });
    };

    const finishResolution = async () => {
        // Defense in depth — the pack/refund controls that lead here are
        // disabled below, so this should be unreachable in read-only mode,
        // but the write must never fire even if something upstream slips
        // through.
        if (readOnly) return;
        const resolved = buildResolvedOrder();
        if (!resolved) return;
        const outcome = await persistResolution(resolved);
        if (outcome === "conflict") {
            // The flag was closed under this page (resolved elsewhere, or
            // this leader's own double-submit) — nothing here can finish
            // it, so go back to the refreshed list.
            playError();
            toast.dismiss();
            toast.error(t("resolve_conflict"));
            navigate("/dispatch/flagged");
            return;
        }
        if (outcome !== "ok") {
            playError();
            toast.dismiss();
            toast.error(outcome === "forbidden"
                ? t("resolve_not_permitted")
                : t("resolve_failed"));
            return;
        }
        playComplete();
        setSuccess(true);
        toast.dismiss();
        if (totalRefund > 0) {
            toast.success(t("refund_issued_for", {
                amount: formatMoney(totalRefund, resolved.currency || "GBP"),
            }));
        }
        setTimeout(() => navigate("/dispatch/flagged"), 900);
    };

    // Read refunds from the latest order state (the callbacks below can
    // fire in the same tick as the last update).
    const currentHasRefunds = () => !!order && order.items.some(i => (i.missingCount || 0) > 0);

    // Pack screen finished with a booked shipment. Detour via the refund
    // summary when any unit was refunded; otherwise straight to Confirm
    // Shipment for label printing.
    const handlePackComplete = (result: CourierBookingResult, updatedBoxes: DispatchBox[]) => {
        setCourierResult(result);
        setBoxes(updatedBoxes);
        setView(currentHasRefunds() ? "refund-summary" : "confirm-ship");
    };

    // Pack screen finished with NOTHING to ship (every unit refunded).
    const handleFinishNoShipment = () => {
        setCourierResult(null);
        if (currentHasRefunds()) setView("refund-summary");
        else finishResolution();
    };

    const handleRefundConfirmed = () => {
        if (courierResult) setView("confirm-ship");
        else finishResolution();
    };

    // Safety nets: still fetching shows a loader; only a FAILED load (order
    // resolved elsewhere, URL typed direct, fetch error) shows the miss.
    if (loadState !== "loaded" || !flaggedOrder || !order) {
        return (
            <div className="dispatch-page page-data">
                <div className="page-title-bar">
                    <h1 className="page-title">{t("resolve_order")}</h1>
                    <button className="page-back-btn" onClick={() => navigate("/dispatch/flagged")}>
                        <ArrowLeft size={20} />
                    </button>
                </div>
                {loadState === "loading" ? (
                    <div className="dispatch-flagged-empty">
                        <FlowLoader message={t("loading")} />
                    </div>
                ) : (
                    <div className="dispatch-flagged-empty">
                        <AlertTriangle size={48} strokeWidth={1.5} />
                        <p>{t("no_order_found")}</p>
                    </div>
                )}
            </div>
        );
    }

    // ONE completion screen for the whole resolution. The pack surface's
    // own "ORDER SHIPPED" overlay is suppressed (suppressSuccessOverlay on
    // DispatchCloseOrder) — leaders were sitting through two full-screen
    // green overlays back to back. The shipment is named here instead, so
    // the confirmation still says a parcel went out.
    if (success) {
        return (
            <div className="dispatch-ship-success-overlay">
                <CheckCircle strokeWidth={3} />
                <h1>{t("resolve_complete")}</h1>
                {courierResult && (
                    <p className="dispatch-ship-success-sub">{t("order_shipped")}</p>
                )}
            </div>
        );
    }

    // Full-page refund summary. Layout mirrors the Confirm Shipment page:
    // title bar, summary cards, totals, big CTA.
    if (view === "refund-summary") {
        const refundItems = order.items.filter(i => refundUnits(i) > 0);
        return (
            <div className="dispatch-page page-data" style={{ height: "calc(100vh - 56px)", overflow: "auto" }}>
                <div className="page-title-bar">
                    <h1 className="page-title">{t("refund_summary")}</h1>
                    <button className="page-back-btn" onClick={() => setView("pack")}>
                        <ArrowLeft size={20} />
                    </button>
                </div>

                {refundItems.length > 0 && (
                    <div className="dispatch-refund-section">
                        <p className="dispatch-refund-section-title">{t("items_to_refund")}</p>
                        <div className="dispatch-refund-list">
                            {refundItems.map(i => (
                                <div key={i.item_id} className="dispatch-refund-card">
                                    {i.image_url ? (
                                        <img src={i.image_url} alt="" className="dispatch-item-thumb" />
                                    ) : (
                                        <div className="dispatch-item-thumb dispatch-item-thumb--empty" />
                                    )}
                                    <div className="dispatch-refund-card-info">
                                        <p className="dispatch-item-title">{i.title || i.sku}</p>
                                        <p className="dispatch-item-sku">
                                            {displayItemCode(i.sku, i.barcode)}
                                            <span style={{ opacity: 0.7 }}>
                                                {t("unit_price")}: {formatMoney(i.unit_price || 0, order.currency || "GBP")}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="dispatch-refund-card-totals">
                                        <span className="dispatch-refund-line-qty">x{refundUnits(i)}</span>
                                        <span className="dispatch-refund-line-total">
                                            {formatMoney(refundUnits(i) * (i.unit_price || 0), order.currency || "GBP")}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="dispatch-refund-section">
                    <label className="dispatch-refund-shipping-label">
                        <span className="dispatch-refund-shipping-row">
                            <span>{t("shipping_refund")}</span>
                            <span className="dispatch-refund-shipping-paid">
                                {typeof order.shipping_paid === "number" && order.shipping_paid > 0
                                    ? `${t("customer_paid_shipping")}: ${formatMoney(order.shipping_paid, order.currency || "GBP")}`
                                    : t("no_shipping_charged")}
                            </span>
                        </span>
                        <input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            max={order.shipping_paid || undefined}
                            step="0.01"
                            value={shippingRefund}
                            onChange={e => setShippingRefund(e.target.value)}
                            placeholder="0.00"
                            disabled={!order.shipping_paid}
                        />
                    </label>
                </div>

                <div className="dispatch-refund-totals">
                    <div className="dispatch-refund-total-row">
                        <span>{t("items_refund_total")}</span>
                        <span>{formatMoney(itemsRefundTotal, order.currency || "GBP")}</span>
                    </div>
                    <div className="dispatch-refund-total-row">
                        <span>{t("shipping_refund")}</span>
                        <span>{formatMoney(shippingRefundValue, order.currency || "GBP")}</span>
                    </div>
                    <div className="dispatch-refund-total-row dispatch-refund-total-row--grand">
                        <span>
                            {/* Full refund = nothing left or leaves the building:
                                no leader shipment booked AND (user-flagged, so
                                nothing shipped originally either). */}
                            {totalRefund === 0
                                ? t("no_refund")
                                : (!courierResult && isUserFlagged && order.items.every(i => shippedUnits(i) === 0))
                                    ? t("full_refund")
                                    : t("partial_refund")}
                        </span>
                        <span>{formatMoney(totalRefund, order.currency || "GBP")}</span>
                    </div>
                </div>

                {totalRefund > 0 && !refundsViaChannel(order) && (
                    <p className="dispatch-refund-manual-note">
                        <Info size={14} /> {t("refund_manual_channel_note")}
                    </p>
                )}

                <div className="dispatch-refund-footer">
                    <button
                        className="btn btn-success btn-xl btn-full"
                        onClick={handleRefundConfirmed}
                        disabled={readOnly}
                        aria-disabled={readOnly}
                        title={readOnly ? t("read_only_mode") : undefined}
                    >
                        <CheckCircle strokeWidth={3} size={22} />
                        {courierResult ? t("process_refund_and_continue") : t("process_refund")}
                    </button>
                </div>
            </div>
        );
    }

    // Confirm-ship view: hand over to the standard Confirm Shipment screen
    // so the leader prints labels and completes the outgoing parcels.
    if (view === "confirm-ship" && courierResult) {
        const shipmentOrder: DispatchOrder = {
            ...order,
            items: order.items.map(i =>
                shippedUnits(i) > 0 ? { ...i, scanStatus: "scanned" as const } : i,
            ),
        };
        return (
            <DispatchSessionProvider stats={stats}>
            <DispatchCloseOrder
                order={shipmentOrder}
                courierResult={courierResult}
                deskInfo={deskInfo}
                boxes={boxes}
                autoPrint={getAutoPrint()}
                onShipComplete={finishResolution}
                suppressSuccessOverlay
                shipShort
                goBack={() => { setCourierResult(null); setView("pack"); }}
            />
            </DispatchSessionProvider>
        );
    }

    // The pack surface — the main dispatch pack screen in resolve mode.
    return (
        <DispatchSessionProvider stats={stats}>
        <DispatchOrderPack
            order={order}
            boxes={boxes}
            deskInfo={deskInfo}
            suppressAutoModal={restoredFromSnapshot}
            onUpdateOrder={setOrder}
            onUpdateBoxes={setBoxes}
            onComplete={handlePackComplete}
            goBack={() => navigate("/dispatch/flagged")}
            resolveMode={{
                onFinishNoShipment: handleFinishNoShipment,
                flagInfo,
                canRefund,
            }}
        />
        </DispatchSessionProvider>
    );
};

export default DispatchFlaggedResolve;
